/**
 * DSCVRD — card bookmarks (wishlist-lite).
 * Any [data-ds-save][data-handle] toggles the handle in the shared
 * localStorage list (dscvrd:saved) also used by the PDP bookmark and
 * the header's Saved dialog. Click handling is delegated so cards
 * added by carousels/section renders keep working.
 */
(() => {
  const KEY = 'dscvrd:saved';
  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  };
  const write = (arr) => {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch {}
  };

  const paint = (scope) => {
    const saved = read();
    (scope || document).querySelectorAll('[data-ds-save][data-handle]').forEach((btn) => {
      const on = saved.includes(btn.dataset.handle);
      btn.classList.toggle('is-saved', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ds-save][data-handle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = btn.dataset.handle;
    const saved = read();
    write(saved.includes(handle) ? saved.filter((h) => h !== handle) : [...saved, handle]);
    paint();
    document.dispatchEvent(new CustomEvent('dscvrd:saved:changed'));
  });

  paint();
  document.addEventListener('shopify:section:load', (e) => paint(e.target));
  document.addEventListener('dscvrd:saved:changed', () => paint());
})();

/**
 * DSCVRD — scroll reveals.
 * Elements opt in with [data-ds-reveal]; optional stagger via
 * --ds-reveal-i set inline. CSS handles the transition and the
 * prefers-reduced-motion fallback (elements stay visible).
 */
(() => {
  const els = document.querySelectorAll('[data-ds-reveal]');
  if (!els.length) return;

  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-inview'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          io.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
  );

  els.forEach((el) => io.observe(el));
})();

/**
 * DSCVRD — product-card quick-add size picker.
 * The card's circular "+" (rendered in snippets/quick-add.liquid for
 * multi-variant products) opens a small size popover instead of
 * Horizon's stock modal, which was empty on the custom PDP. Picking a
 * size writes the chosen variant into the card's product form and
 * submits it, so Horizon's product-form-component runs its normal
 * AJAX add + cart drawer. Delegated so cards injected by carousels or
 * section re-renders keep working.
 */
(() => {
  const closeAll = (except) => {
    document.querySelectorAll('[data-dsq].is-open').forEach((dsq) => {
      if (dsq === except) return;
      dsq.classList.remove('is-open');
      dsq.querySelector('[data-dsq-toggle]')?.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-dsq-toggle]');
    if (toggle) {
      event.preventDefault();
      const dsq = toggle.closest('[data-dsq]');
      const open = !dsq.classList.contains('is-open');
      closeAll(dsq);
      dsq.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const size = event.target.closest('[data-dsq-variant]');
    if (size) {
      event.preventDefault();
      if (size.disabled) return;
      const form = size.closest('form');
      const idInput = form?.querySelector('input[name="id"]');
      if (!form || !idInput) return;
      idInput.value = size.dataset.dsqVariant;
      idInput.removeAttribute('disabled');
      closeAll(null);
      // Hand off to Horizon's product-form-component (AJAX add + drawer).
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
      return;
    }

    // A click anywhere else (outside an open panel) dismisses it.
    if (!event.target.closest('[data-dsq-panel]')) closeAll(null);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll(null);
  });
})();
